import { useRef } from "react";

const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
const ARABIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";

function normalizeDigits(value) {
  return String(value || "")
    .replace(/[۰-۹]/g, (digit) => String(PERSIAN_DIGITS.indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String(ARABIC_DIGITS.indexOf(digit)))
    .replace(/[^0-9]/g, "");
}

export default function OtpCode({
  values,
  setValues,
  onValueChange,
  inputClassName = "",
  className = "flex justify-between items-center gap-4 [direction:ltr]",
}) {
  const inputRefs = useRef([]);
  const codeLength = values.length;

  const applyDigits = (rawValue, requestedStartIndex) => {
    const digits = normalizeDigits(rawValue);

    if (!digits) return false;

    const startIndex = digits.length >= codeLength ? 0 : requestedStartIndex;
    const availableLength = codeLength - startIndex;
    const nextDigits = digits.slice(0, availableLength);

    setValues((previousValues) => {
      const updatedValues = [...previousValues];

      nextDigits.split("").forEach((digit, offset) => {
        updatedValues[startIndex + offset] = digit;
      });

      return updatedValues;
    });
    onValueChange?.();

    const nextIndex = Math.min(startIndex + nextDigits.length, codeLength - 1);
    window.requestAnimationFrame(() => inputRefs.current[nextIndex]?.focus());

    return true;
  };

  const handleChange = (event, index) => {
    const rawValue = event.currentTarget.value;
    const digits = normalizeDigits(rawValue);

    if (digits.length > 1) {
      applyDigits(digits, index);
      return;
    }

    setValues((previousValues) => {
      const updatedValues = [...previousValues];
      updatedValues[index] = digits;
      return updatedValues;
    });
    onValueChange?.();

    if (digits && index < codeLength - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event, index) => {
    const pastedValue = event.clipboardData?.getData("text");

    // Android Chrome can omit clipboardData. In that case, allow the native
    // paste and distribute the resulting value from handleChange instead.
    if (!pastedValue) return;

    if (applyDigits(pastedValue, index)) {
      event.preventDefault();
    }
  };

  return (
    <div className={className}>
      {values.map((digit, index) => (
        <input
          key={index}
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          type="text"
          value={digit}
          maxLength={codeLength}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="one-time-code"
          enterKeyHint={index === codeLength - 1 ? "done" : "next"}
          aria-label={`رقم ${index + 1} کد یک‌بار مصرف`}
          className={inputClassName}
          onFocus={(event) => event.currentTarget.select()}
          onChange={(event) => handleChange(event, index)}
          onPaste={(event) => handlePaste(event, index)}
          onKeyDown={(event) => {
            if (event.key === "Backspace" && !digit) {
              inputRefs.current[index - 1]?.focus();
            }
            if (event.key === "ArrowLeft") {
              event.preventDefault();
              inputRefs.current[index - 1]?.focus();
            }
            if (event.key === "ArrowRight") {
              event.preventDefault();
              inputRefs.current[index + 1]?.focus();
            }
          }}
        />
      ))}
    </div>
  );
}
